import {
  Heart,
  Users,
  Target,
  Sparkles,
  ArrowRight,
  GraduationCap,
} from "lucide-react";

const values = [
  {
    icon: Heart,
    title: "Inclusion",
    text: "Every kid belongs. No tryouts, no sidelines — just teammates.",
  },
  {
    icon: Users,
    title: "Community",
    text: "We build lasting friendships between players, families, and volunteers.",
  },
  {
    icon: Target,
    title: "Confidence",
    text: "Through basketball, kids discover what they're capable of — on and off the court.",
  },
];

const founders = [
  {
    name: "Taylor Katz",
    paragraphs: [
      "Taylor Katz is a student at Tamalpais High School with a strong passion for working with kids and building inclusive communities through sports.",
      "He has been volunteering with PAASS (Project Awareness and Special Sports), where he helps teach and play sports with kids with intellectual and developmental disabilities.",
      "In addition, Taylor works as a coach at Lagarza Basketball Camps, working with kids ages 6–13, and works at Mathnasium, where he teaches and supports students in math.",
      "He also started and organized a basketball team for students in his grade who did not make the school team, helping them compete in a local league and stay involved in the game.",
      "Next year, Taylor will serve as a teacher's assistant for a special education class at his high school, continuing his commitment to supporting students both on and off the court.",
    ],
  },
  {
    name: "Julian MacColl",
    paragraphs: [
      "Julian MacColl is a student-athlete at Tamalpais High School who is passionate about sports and creating opportunities for others.",
      "He has also volunteered with PAASS, working directly with kids with intellectual and developmental disabilities and helping create a fun and supportive environment through sports.",
      "Julian is a varsity golfer at his high school and has experience mentoring and leading others through athletics.",
      "He also coached a basketball team for students in his grade who did not make the school team, helping them stay involved in the game and compete together.",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 md:pt-24 md:pb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 text-orange-700 text-sm font-extrabold uppercase tracking-wider">
            <Sparkles className="w-4 h-4" />
            About UHL
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            A place to play, grow, and{" "}
            <span className="text-blue-600">have fun.</span>
          </h1>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="pb-8" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
              Our Story
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              How UHL started.
            </h2>

            <div className="mt-6 space-y-5 text-slate-700 text-lg font-medium leading-relaxed">
              <p>
                Unified Hoops League was started by two students at Tamalpais
                High School who have spent years working with kids with
                intellectual and developmental disabilities through sports.
              </p>
              <p>
                After seeing how much these kids loved playing — but didn't
                always have consistent opportunities — we wanted to create a
                program where every player could feel included, supported,
                and part of a team.
              </p>
              <p>
                UHL is built on the idea that everyone deserves a place to
                play, grow, and have fun.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="founders-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
              The Founders
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Meet the team behind UHL.
            </h2>
          </div>

          {/* FOUNDERS PHOTO */}
          <figure className="mt-10 max-w-3xl">
            <div className="rounded-3xl overflow-hidden border-[6px] border-white shadow-2xl shadow-blue-900/10">
              <img
                src="https://customer-assets.emergentagent.com/job_ubl-hoops/artifacts/i9wypdjz_IMG_0942.jpeg"
                alt="Taylor Katz and Julian MacColl, Co-Founders of UHL"
                data-testid="founders-photo"
                className="w-full h-auto object-cover"
              />
            </div>
            <figcaption className="mt-3 text-sm font-semibold text-slate-500 italic">
              Taylor Katz (left) and Julian MacColl (right), Co-Founders of UHL.
            </figcaption>
          </figure>

          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            {founders.map((person) => (
              <article
                key={person.name}
                data-testid={`founder-${person.name.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-[#FAFAFA] rounded-3xl p-8 sm:p-10 border border-slate-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight">
                      {person.name}
                    </h3>
                    <p className="mt-1 text-sm font-black uppercase tracking-[0.15em] text-orange-500">
                      Co-Founder
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-4 text-slate-700 font-medium leading-relaxed">
                  {person.paragraphs.map((para, i) => (
                    <p key={i}>
                      {i === 0 ? (
                        <>
                          <strong className="font-black text-slate-900">
                            {person.name}
                          </strong>
                          {para.slice(person.name.length)}
                        </>
                      ) : (
                        para
                      )}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PAASS PARTNERSHIP */}
      <section
        className="pb-8"
        data-testid="paass-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-blue-50 rounded-3xl border border-blue-100 p-8 sm:p-10 flex flex-col md:flex-row items-center gap-8">
            <img
              src="https://customer-assets.emergentagent.com/job_ubl-hoops/artifacts/k2t3nrpa_Screenshot%202026-05-14%20at%2011.07.22%E2%80%AFAM.png"
              alt="PAASS — Project Awareness and Special Sports"
              className="w-32 h-32 sm:w-36 sm:h-36 object-contain flex-shrink-0"
            />
            <div className="text-center md:text-left flex-1">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">
                Officially Partnered With
              </p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-black text-slate-900">
                PAASS — Project Awareness and Special Sports
              </h3>
              <p className="mt-3 text-slate-700 font-medium leading-relaxed max-w-2xl">
                UHL is officially partnered with PAASS, a community
                organization dedicated to creating inclusive sports
                experiences for kids with intellectual and developmental
                disabilities. Both founders have volunteered with PAASS for
                years, and that experience is the foundation UHL is built on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
              Our Mission
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Basketball for every kid — no exceptions.
            </h2>
            <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600">
              We believe sports should be a place where kids feel seen,
              celebrated, and supported. UHL exists to make sure that kids
              with intellectual and developmental disabilities have a weekly
              space to play, grow, and belong.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {values.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`value-${title.toLowerCase()}`}
                className="bg-white rounded-3xl p-8 border border-slate-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="mt-5 text-xl font-black text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-blue-50 rounded-3xl p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900">
                Join UHL
              </h3>
              <p className="mt-2 text-slate-600 font-medium text-lg">
                Sign your child up, or join us as a volunteer.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Reserve%20My%20Spot%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20reserve%20a%20spot%20for%20my%20player.%0A%0AParent%20name%3A%20%0APlayer%27s%20name%20%26%20age%3A%20%0AAny%20notes%3A%20"
                data-testid="about-register-btn"
                className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 transition-all font-bold px-7 py-3 rounded-full"
              >
                📩 Reserve Your Spot <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Volunteer%20Early%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20volunteer.%0A%0AName%3A%20%0ASchool%20%26%20grade%3A%20"
                data-testid="about-volunteer-btn"
                className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-white transition-all font-bold px-7 py-3 rounded-full"
              >
                📩 Volunteer Early
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
